import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@cieda/djayma-ui";
import { Eye } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import {
  AdminDataTable,
  StatusBadge,
  type ColumnDef,
  type PaginationState,
  type SortState,
} from "@/components/admin/shared";
import {
  PAYMENT_METHOD_I18N_KEYS,
  type OrderSummary,
  type PaymentMethod,
} from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrdersTableProps {
  readonly data: readonly OrderSummary[];
  readonly pagination: PaginationState;
  readonly isLoading: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onSort?: (sort: SortState) => void;
  readonly onRowSelect?: (ids: readonly string[]) => void;
  readonly currentSort?: SortState | null;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const fcfaFormatter = new Intl.NumberFormat("fr-SN", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatFCFA(amount: number | null | undefined): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `${fcfaFormatter.format(n)} FCFA`;
}

function formatDate(iso: string): string {
  const d = parseISO(iso);
  if (!isValid(d)) return "—";
  return format(d, "dd/MM/yyyy");
}

function isKnownPaymentMethod(value: string): value is PaymentMethod {
  return value in PAYMENT_METHOD_I18N_KEYS;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrdersTable({
  data,
  pagination,
  isLoading,
  onPageChange,
  onSort,
  onRowSelect,
  currentSort,
}: OrdersTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("admin");

  const columns: readonly ColumnDef<OrderSummary>[] = useMemo(
    () => [
      {
        id: "reference",
        header: t("orders.col_reference", "Référence"),
        accessorKey: "reference",
        sortable: true,
        mobileVisible: true,
      },
      {
        id: "customer_name",
        header: t("orders.col_client", "Client"),
        accessorKey: "customer_name",
        sortable: true,
        mobileVisible: true,
      },
      {
        id: "total_ttc",
        header: t("orders.col_amount", "Montant TTC"),
        sortable: true,
        mobileVisible: true,
        cell: (row) => (
          <span className="font-medium tabular-nums">{formatFCFA(row.total_ttc)}</span>
        ),
      },
      {
        id: "status",
        header: t("orders.col_status", "Statut"),
        sortable: false,
        mobileVisible: true,
        cell: (row) => <StatusBadge status={row.status} variant="order" />,
      },
      {
        id: "payment_method",
        header: t("orders.col_payment", "Paiement"),
        sortable: false,
        mobileVisible: false,
        cell: (row) => {
          const label = isKnownPaymentMethod(row.payment_method)
            ? t(PAYMENT_METHOD_I18N_KEYS[row.payment_method])
            : row.payment_method;
          return <span className="text-stone-600">{label}</span>;
        },
      },
      {
        id: "created_at",
        header: t("orders.col_date", "Date"),
        sortable: true,
        mobileVisible: false,
        cell: (row) => (
          <span className="tabular-nums text-stone-500">{formatDate(row.created_at)}</span>
        ),
      },
      {
        id: "actions",
        header: t("orders.col_actions", "Actions"),
        sortable: false,
        mobileVisible: false,
        cell: (row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/orders/${row.id}`)}
            className="gap-1.5"
            aria-label={t("orders.action_view", "Voir la commande")}
          >
            <Eye className="h-4 w-4" />
            <span className="hidden lg:inline">
              {t("orders.action_view_short", "Voir")}
            </span>
          </Button>
        ),
      },
    ],
    [t, navigate],
  );

  return (
    <AdminDataTable<OrderSummary>
      columns={columns}
      data={data}
      pagination={pagination}
      isLoading={isLoading}
      onPageChange={onPageChange}
      onSort={onSort}
      onRowSelect={onRowSelect}
      currentSort={currentSort}
      emptyMessage={t("orders.empty", "Aucune commande trouvée.")}
      getRowId={(row) => row.id}
    />
  );
}
